@if (terminalWorkBatchCtx$ | async; as ctx) {
  <elder-basic-pane-layout class="full">
    <elder-pane>
      <elder-pane-content>
        <div class="flex-none layout-row gap-sm place-start-center p-sm">
          <elder-header
            header="{{ 'batches.batch' | translate }} {{
              (terminalBatch$ | async)?.workBatch?.number
            }}"
            subheader="{{ 'orders.work.order' | translate }} {{ workOrderId$ | async }}"
          >
          </elder-header>
          <span class="flex"></span>
          <div class="layout-col place-center-center gap-sm">
            <div class="flex layout-row place-center-center gap-sm">
              @for (productionCode of ctx.terminalBatch.productionCodes; track productionCode) {
                <mat-chip highlighted>{{ productionCode.code }}</mat-chip>
              }
            </div>
            @if (
              ctx.terminalBatch.workOrder.manufactureFacility !==
              ctx.terminalBatch.workOrder.collectingFacility
            ) {
              <div class="flex" class="layout-row place-start-center gap-lg">
                <span>{{ ctx.terminalBatch.workOrder.manufactureFacility }}</span>
                <mat-icon color="warn">east</mat-icon>
                <span>{{ ctx.terminalBatch.workOrder.collectingFacility }}</span>
              </div>
            }
          </div>
          <span class="flex"></span>
          <!-- Status -->
          @if (ctx.terminalBatch.workBatch.state) {
            <app-batch-status-indicator
              [workBatchState]="ctx.terminalBatch.workBatch.state"
            ></app-batch-status-indicator>
          }
          @if (storageOverview$ | async; as overview) {
            <mat-form-field [floatLabel]="'always'" elderFormFieldLabel elderDense>
              <mat-label>{{ 'shopfloor.storage.setupStation' | translate }}</mat-label>
              <input
                matInput
                name="baseLocation"
                readonly
                placeholder="-"
                [ngModel]="overview.baseLocation.path"
              />
            </mat-form-field>
          }
          <button
            mat-flat-button
            color="warn"
            [disabled]="!canAbort(ctx)"
            (click)="abortBatch($event)"
          >
            {{ 'actions.cancel' | translate }}
          </button>
          <app-work-batch-line [batch]="ctx.terminalBatch.workBatch"></app-work-batch-line>
        </div>
        <mat-tab-group class="flex" animationDuration="0" [(selectedIndex)]="selectedTabIndex">
          <!-- WorkItem table -->
          <mat-tab>
            <div *matTabLabel class="layout-col">
              <span>{{ 'context.items' | translate }}</span>
            </div>
            <div class="layout-col full">
              <elder-table
                class="flex"
                matSort
                [data]="data"
                [idField]="'id'"
                (itemClick)="openDetail($event)"
              >
                <elder-data-toolbar
                  *elderTableToolbar
                  [canMore]="true"
                  [canAdd]="false"
                  [canRemove]="false"
                >
                  <ng-template elderToolbarContent="left.actions">
                    @if (workBatchSegment$ | async; as workBatchSegment) {
                      <div class="layout-row gap-md pt-md">
                        <!-- Start Button -->
                        <button
                          mat-flat-button
                          color="primary"
                          matTooltip="{{
                            'WorkBatchSegmentState.' + workBatchSegment?.state | translate
                          }}"
                          (click)="startBatchSegment()"
                          [disabled]="!canStartBatch(ctx.terminalBatch.workBatch)"
                        >
                          <mat-icon>play_arrow</mat-icon>
                          {{ 'context.start' | translate }}
                        </button>
                        <!-- Print Button -->
                        <button
                          mat-flat-button
                          color="primary"
                          (click)="provideItemLabels()"
                          [disabled]="!canProvideItemLabels(ctx)"
                          [matBadge]="'!P'"
                          [matBadgeColor]="'warn'"
                          [matBadgeHidden]="
                            !!ctx.terminal?.labelPrinter?.printerId || !canProvideItemLabels(ctx)
                          "
                        >
                          <div class="layout-row place-start-center gap-sm">
                            <mat-icon>printer</mat-icon>
                            <imprimo-output-job-indicator
                              class="flex-none"
                              [asyncJob]="asyncJob$ | async"
                            ></imprimo-output-job-indicator>
                          </div>
                        </button>
                        <!-- Retrieval Button -->
                        <button
                          mat-flat-button
                          color="primary"
                          (click)="transferBatchSegment()"
                          [disabled]="!canTransferBatchSegment(ctx, workBatchSegment)"
                          [matTooltip]="workBatchSegment?.transferError?.title"
                          [matBadge]="transferSymbol(workBatchSegment)"
                          [matBadgeHidden]="workBatchSegment?.transferState === TransferState.OPEN"
                          [matBadgeColor]="
                            workBatchSegment?.transferState === TransferState.FAILED
                              ? 'warn'
                              : 'accent'
                          "
                        >
                          <mat-icon>usb</mat-icon>
                          {{ 'context.retrieval' | translate }}
                        </button>
                        <!-- Packages Button -->
                        <button
                          mat-flat-button
                          color="primary"
                          [disabled]="packagesDisabled(ctx, workBatchSegment)"
                          (click)="switchToPackageView()"
                          [matTooltip]="
                            getPackagingToolTip(workBatchSegment, packageTable.data$() | async)
                          "
                          [matBadge]="'!'"
                          [matBadgeHidden]="
                            shouldHidePackagesBadge(
                              ctx,
                              workBatchSegment,
                              packageTable.data$() | async
                            )
                          "
                          [matBadgeColor]="'warn'"
                        >
                          <div class="layout-row place-center-center gap-sm">
                            @if (finalizesPackaging(workBatchSegment)) {
                              <mat-icon>featured_video</mat-icon>
                            }
                            <span>{{ 'packages.packages' | translate }}</span>
                          </div>
                        </button>
                        <!-- Batch Tracing Button -->
                        <button
                          mat-flat-button
                          color="primary"
                          [disabled]="
                            !this.hasStarted(ctx) || !(batchTracingCtx$ | async)?.canConfigure
                          "
                          (click)="openBatchTracingConfDetail()"
                          [matBadge]="'!'"
                          [matBadgeHidden]="
                            !hasStarted(ctx) || (batchTracingCtx$ | async)?.isConfigValid
                          "
                          [matBadgeColor]="'warn'"
                        >
                          <div class="layout-row place-center-center gap-sm">
                            @if (finalizesPackaging(workBatchSegment)) {
                              <mat-icon>radar</mat-icon>
                            }
                            <span>{{ 'batchtracing.title-short' | translate }}</span>
                          </div>
                        </button>
                        <!-- Completion Button -->
                        <button
                          mat-flat-button
                          color="primary"
                          [disabled]="
                            !canCompleteBatchSegment(
                              ctx,
                              workBatchSegment,
                              packageTable.data$() | async,
                              batchTracingCtx$ | async
                            )
                          "
                          (click)="completeBatchSegment()"
                        >
                          @if (isLastSegment(ctx, workBatchSegment)) {
                            <div class="layout-row place-center-center gap-sm">
                              <mat-icon>done_all</mat-icon>
                              <span>{{ 'actions.complete' | translate }}</span>
                            </div>
                          } @else {
                            <div class="layout-row place-center-center gap-sm">
                              <mat-icon>done</mat-icon>
                              <span>{{ 'actions.ok' | translate }}</span>
                            </div>
                          }
                        </button>
                      </div>
                    }
                  </ng-template>
                  <ng-template elderToolbarContent="right.actions">
                    @if (workBatchSegment$) {
                      <div class="layout-row gap-md pt-md">
                        <!-- Refresh Production Groups Button -->
                        <button
                          mat-flat-button
                          color="primary"
                          (click)="refreshProductionGroups()"
                          [disabled]="!hasStarted(ctx)"
                        >
                          <mat-icon>refresh</mat-icon>
                          {{ 'context.sort' | translate }}
                        </button>
                      </div>
                    }
                  </ng-template>
                  <button
                    mat-menu-item
                    [disabled]="!canPreviewItemLabels(ctx)"
                    (click)="onPreviewBatchLabels(ctx)"
                  >
                    <mat-icon>preview</mat-icon>
                    <span>{{ 'labels.preview' | translate }}</span>
                  </button>
                  <button
                    mat-menu-item
                    [disabled]="!canPreviewItemLabels(ctx)"
                    (click)="onPreviewItemLabels(ctx)"
                  >
                    <mat-icon>preview</mat-icon>
                    <span>{{ 'labels.items' | translate }}</span>
                  </button>
                  <button
                    mat-menu-item
                    [disabled]="!canPreviewItemLabels(ctx)"
                    (click)="onPreviewPieceLabels(ctx)"
                  >
                    <mat-icon>preview</mat-icon>
                    <span>{{ 'labels.piece' | translate }}</span>
                  </button>
                  <button mat-menu-item (click)="onPreviewProdList(ctx)">
                    <mat-icon>assignment</mat-icon>
                    <span>{{ 'orders.prod.list.title' | translate }}</span>
                  </button>
                </elder-data-toolbar>
                <!-- production priority Column -->
                <ng-container matColumnDef="priority">
                  <th
                    mat-header-cell
                    *matHeaderCellDef
                    class="cell-10"
                    mat-sort-header="{{ getSortByProcessingSequencePosString() }}"
                  >
                    {{ 'context.priority' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let item">{{ getProductionPriority(item) }}</td>
                </ng-container>
                <!-- number Column -->
                <ng-container matColumnDef="number">
                  <th mat-header-cell *matHeaderCellDef class="cell-10" mat-sort-header>
                    {{ 'context.number' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let item">{{ item.number }}</td>
                </ng-container>
                <!-- description Column -->
                <ng-container matColumnDef="description">
                  <th mat-header-cell *matHeaderCellDef class="cell-15">
                    {{ 'context.description' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let item">
                    <div class="layout-col">
                      <span>{{ item.description }}</span>
                      <span class="mat-caption">{{ item.orderedProduct?.materialId }}</span>
                    </div>
                  </td>
                </ng-container>
                <!-- length Column -->
                <ng-container matColumnDef="length">
                  <th
                    mat-header-cell
                    elderNumberCell
                    *matHeaderCellDef
                    mat-sort-header="dimensions.length"
                    arrowPosition="before"
                    class="cell-10 align-right"
                  >
                    {{ 'context.length' | translate }}
                  </th>
                  <td mat-cell elderNumberCell *matCellDef="let item" class="align-right">
                    {{ Math.round(item?.dimensions?.length) }} {{ item?.dimensions?.unit }}
                  </td>
                </ng-container>
                <!-- width Column -->
                <ng-container matColumnDef="width">
                  <th
                    mat-header-cell
                    elderNumberCell
                    *matHeaderCellDef
                    mat-sort-header="dimensions.width"
                    arrowPosition="before"
                    class="cell-10 align-right"
                  >
                    {{ 'context.width' | translate }}
                  </th>
                  <td mat-cell elderNumberCell *matCellDef="let item" class="align-right">
                    {{ Math.round(item?.dimensions?.width) }} {{ item?.dimensions?.unit }}
                  </td>
                </ng-container>
                <!-- mainProductQuantity Column -->
                <ng-container matColumnDef="mainProductQuantity">
                  <th mat-header-cell elderNumberCell *matHeaderCellDef class="cell-10 align-right">
                    {{ 'context.quantity' | translate }}
                  </th>
                  <td mat-cell elderNumberCell *matCellDef="let item" class="align-right">
                    {{ item.producedProduct | productSalesQuantity | elderQuantity }}
                  </td>
                </ng-container>
                <!-- totalWeight Column -->
                <ng-container matColumnDef="totalWeight">
                  <th mat-header-cell elderNumberCell *matHeaderCellDef class="cell-10 align-right">
                    {{ 'context.totalWeight' | translate }}
                  </th>
                  <td mat-cell elderNumberCell *matCellDef="let item" class="align-right">
                    {{ item.producedProduct | productSalesTotalWeight | elderQuantity }}
                  </td>
                </ng-container>
                <!-- package Column -->
                <ng-container matColumnDef="packageId">
                  <th mat-header-cell *matHeaderCellDef class="cell-10 align-right">
                    {{ 'packages.package' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let item" class="align-right">
                    <app-work-item-packages
                      [unitBundles]="item.packages"
                      (packageClick)="packageClick($event)"
                    ></app-work-item-packages>
                  </td>
                </ng-container>
                <!-- processingState Column -->
                <ng-container matColumnDef="processingState">
                  <th mat-header-cell *matHeaderCellDef class="cell-10 align-right">
                    {{ 'context.status' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let item" class="align-right">
                    {{ 'WorkItemState.' + item.processingState | translate }}
                  </td>
                </ng-container>
                <!-- manufacturingStatus Column -->
                <ng-container matColumnDef="manufacturingStatus">
                  <th mat-header-cell *matHeaderCellDef class="cell-10 align-right">
                    {{ 'context.status' | translate }}
                  </th>
                  <td mat-cell *matCellDef="let item" class="align-right">
                    <app-item-manufacturing-status [item]="item"></app-item-manufacturing-status>
                  </td>
                </ng-container>
              </elder-table>
            </div>
          </mat-tab>
          <!-- Package manager -->
          <mat-tab [disabled]="packagesDisabled(ctx, workBatchSegment)">
            <span
              *matTabLabel
              class="noselect"
              [matBadge]="packageTable.currentPackageCount()"
              [matBadgeOverlap]="false"
            >
              {{ 'packages.packages' | translate }}
            </span>
            <app-terminal-batch-packages
              class="flex"
              #packageTable
              style="padding-top: 8px"
              [terminalBatchCtx]="terminalWorkBatchCtx$ | async"
              [workBatchSegment]="workBatchSegment$ | async"
              [storageOverview]="storageOverview$ | async"
              (storageOverviewRefreshRequest)="reloadStorageOverview()"
            >
            </app-terminal-batch-packages>
          </mat-tab>
          <!-- Hints -->
          <mat-tab [disabled]="!hasHints(ctx)">
            <span
              *matTabLabel
              class="noselect"
              [matBadge]="countHints(ctx)"
              [matBadgeOverlap]="false"
            >
              {{ 'context.hints' | translate }}
            </span>

            <div class="flex layout-row wrap gap-lg pt-md">
              <!-- Production Codes -->
              @for (productionCode of ctx.terminalBatch.productionCodes; track productionCode) {
                <app-production-hint
                  class="flex-20"
                  [title]="productionCode.code"
                  [body]="productionCode.defaultMessage"
                ></app-production-hint>
              }
              @if (hasNote(ctx)) {
                <app-production-hint
                  class="flex-20"
                  title="{{ 'context.note' | translate }}"
                  [body]="ctx.terminalBatch.workOrder.note"
                ></app-production-hint>
              }
            </div>
          </mat-tab>
        </mat-tab-group>
      </elder-pane-content>
    </elder-pane>
  </elder-basic-pane-layout>
}
